// Long-form wellness guides — original editorial, written to be genuinely
// useful for Bhubaneswar visitors. No fabricated statistics or claims.

export const GUIDE_ARTICLES = {
  "best-spa-in-bhubaneswar": {
    title: "Best spa in Bhubaneswar: a thoughtful shortlist",
    seoTitle: "Best Spa in Bhubaneswar | A Considered Shortlist",
    description:
      "How to choose the best spa in Bhubaneswar — what to look for, what to ask, and the clear signs of a well-run wellness destination.",
    read: "6 min",
    kicker: "A practical shortlist",
    intro:
      "There is no single best spa for every guest, every day. There are, however, consistent signs of a well-run wellness destination — and once you know what to look for, choosing becomes quick. Here is the shortlist we use when we think about where to send a friend.",
    sections: [
      {
        h: "Transparent pricing",
        body: [
          "A professional spa shares its full price list before you commit. If a venue hesitates to confirm what each service costs or what's included, that's a reliable red flag. The clearer the number, the more likely the experience will match what you expect at the door.",
          "Zorba International Spa in Chandrasekharpur publishes exact numbers for every service across 60, 90 and 120 minute options. You can read them on the pricing page or ask over WhatsApp before you book.",
        ],
      },
      {
        h: "A quiet, considered space",
        body: [
          "The reception should feel calm from the moment you walk in. Treatment rooms should be clean, properly lit for a massage (soft, not harsh) and private enough that you forget what's outside.",
          "Pay attention to the small things: fresh towels, the condition of linens, oil temperature, the quality of the sheets, the music. These add up quickly.",
        ],
      },
      {
        h: "Trained therapists",
        body: [
          "A good therapist asks a few questions before starting — your preferred pressure, areas you'd like focus on, any injuries. They adjust pace within the first ten minutes based on how your body is responding. They never push past your comfort limit.",
        ],
      },
      {
        h: "Clear communication",
        body: [
          "Can you reach the spa easily over WhatsApp or phone? Do they confirm your appointment and the service you booked? Do they explain inclusions clearly? These signal how the rest of the visit will go.",
        ],
      },
    ],
    relatedSlugs: ["spa-price-guide-bhubaneswar", "first-spa-visit"],
  },

  "spa-price-guide-bhubaneswar": {
    title: "Spa price guide for Bhubaneswar",
    seoTitle: "Spa Price Guide Bhubaneswar | Transparent Pricing",
    description:
      "A plain, practical guide to spa prices in Bhubaneswar — what each service typically costs, what affects the price, and how to compare fairly.",
    read: "6 min",
    kicker: "Transparent pricing",
    intro:
      "Spa prices in Bhubaneswar vary with the style of massage, the duration, and the spa itself. The clearest way to think about pricing is to compare three things: what's included, how long the session is, and what the therapist's experience looks like. The number by itself tells you less than you'd expect.",
    sections: [
      {
        h: "Published starting points at our featured partner",
        body: [
          "Thai massage: 60 min from ₹2,500.",
          "Aroma therapy: 60 min from ₹2,800.",
          "Swedish therapy: 60 min from ₹3,000.",
          "Deep tissue: 60 min from ₹3,500.",
          "Zorba signature journey: 90 min for ₹6,000.",
          "Couples suite: 60 min from ₹6,500; 120 min at ₹12,500.",
        ],
      },
      {
        h: "What makes a price higher",
        body: [
          "Longer durations (90 and 120 minutes carry a modest premium over 60).",
          "Services that require specialist tools or warmed stones.",
          "Private suite bookings for couples or small groups.",
          "Signature packages that layer multiple services into one flow.",
        ],
      },
      {
        h: "Questions to ask before booking",
        body: [
          "Does the quoted price include taxes?",
          "Are there any add-ons that could change the final amount?",
          "What is the cancellation and rescheduling policy?",
          "Is a seasonal offer currently valid?",
        ],
      },
    ],
    relatedSlugs: ["massage-price-guide-bhubaneswar", "best-spa-in-bhubaneswar"],
  },

  "massage-price-guide-bhubaneswar": {
    title: "Massage price guide for Bhubaneswar",
    seoTitle: "Massage Price Guide Bhubaneswar | What to Expect",
    description:
      "A simple guide to massage prices in Bhubaneswar — comparing the main styles, what each costs, and how to pick without overpaying.",
    read: "5 min",
    kicker: "What each style costs",
    intro:
      "Massage prices in Bhubaneswar sit on a predictable range once you know the style and duration. Here's a plain look at what each service typically costs at a professional spa — and where the real differences come from.",
    sections: [
      {
        h: "Starting prices by style (verified)",
        body: [
          "Thai massage: ₹2,500 / 60 min.",
          "Aroma therapy: ₹2,800 / 60 min.",
          "Swedish massage: ₹3,000 / 60 min.",
          "Deep tissue massage: ₹3,500 / 60 min.",
          "Balinese massage: ₹3,500 / 60 min.",
          "Hot stone massage: ₹4,500 / 60 min.",
          "Four hands massage: ₹5,000 / 60 min.",
        ],
      },
      {
        h: "The usual trade-offs",
        body: [
          "Lighter styles (Swedish, aroma) are the most accessibly priced starting points.",
          "Firmer, more focused work (deep tissue, sport) is a modest step up.",
          "Specialist techniques (hot stone, four hands, bamboo) tend to be priced higher because of the technique or tools involved.",
          "The signature 6-in-1 journey at Zorba is ₹6,000 for 90 minutes — a reasonable choice when you want everything in one visit.",
        ],
      },
    ],
    relatedSlugs: ["spa-price-guide-bhubaneswar", "swedish-vs-deep-tissue"],
  },

  "swedish-vs-deep-tissue": {
    title: "Swedish vs deep tissue: which one is right for you?",
    seoTitle: "Swedish vs Deep Tissue Massage | How to Choose",
    description:
      "A simple comparison of Swedish and deep tissue massage — pressure, pace, best use cases, and how to pick the right one for your day.",
    read: "4 min",
    kicker: "Pick the right style",
    intro:
      "Swedish and deep tissue are the two most-booked massage styles. They look similar from the outside, but the experience is very different. A short, honest comparison to help you choose.",
    sections: [
      {
        h: "Swedish — the gentler start",
        body: [
          "Long, flowing strokes with warmed oil. The pressure stays light to medium. The pace is unhurried.",
          "Best for first-time guests, anyone winding down after a long week, and travellers who've just arrived in the city.",
        ],
      },
      {
        h: "Deep tissue — focused relief",
        body: [
          "Slower strokes with sustained, firmer pressure on specific areas of tension — shoulders, lower back, legs.",
          "Best for desk workers with chronic tightness, people who exercise consistently, or anyone recovering soreness from long travel.",
        ],
      },
      {
        h: "A simple rule",
        body: [
          "If you want to feel calm, choose Swedish. If you want to feel released, choose deep tissue. If you want both — the signature journey is designed for exactly that.",
        ],
      },
    ],
    relatedSlugs: ["thai-massage-guide", "first-spa-visit"],
  },

  "thai-massage-guide": {
    title: "Thai massage: a short, practical guide",
    seoTitle: "Thai Massage Guide | What to Expect",
    description:
      "What a Thai massage actually involves, who it suits, and how to prepare for your first session.",
    read: "5 min",
    kicker: "Assisted stretching, no oil",
    intro:
      "Thai massage is quite different from a standard oil massage. You stay in comfortable clothing, lie on a mat, and the therapist guides your body through gentle stretches alongside acupressure. The result is a feeling of openness through the hips, back and shoulders — and a surprisingly calm mind.",
    sections: [
      {
        h: "What happens during the session",
        body: [
          "You wear or arrive in loose clothing you can stretch in.",
          "The therapist uses palms, thumbs, forearms and body weight to apply pressure.",
          "A sequence of gentle stretches works through hips, shoulders, legs and back.",
          "No oil is used — the whole session is dry.",
        ],
      },
      {
        h: "Who it tends to suit",
        body: [
          "People who sit a lot and want to feel mobile again.",
          "Guests who prefer no oil.",
          "Anyone curious about traditional bodywork styles.",
        ],
      },
      {
        h: "How to prepare",
        body: [
          "Avoid heavy meals in the hour before the session.",
          "Share any knee, hip or shoulder issues ahead of time.",
          "Hydrate well through the day.",
        ],
      },
    ],
    relatedSlugs: ["swedish-vs-deep-tissue", "first-spa-visit"],
  },

  "couple-massage-guide": {
    title: "Planning a couples massage in Bhubaneswar",
    seoTitle: "Couple Massage Bhubaneswar | How to Plan",
    description:
      "A clear, practical guide to planning a couples spa experience in Bhubaneswar — what's included, how long to book, and small tips that make it better.",
    read: "5 min",
    kicker: "A shared ritual",
    intro:
      "A couples spa visit is more than two parallel bookings. The best versions feel like a quiet pocket of shared time — a private candle-lit room, two therapists in sync, and nowhere else you need to be for the next hour or two. Here's how to plan one well.",
    sections: [
      {
        h: "What a couples session looks like at Zorba",
        body: [
          "A private couples suite with two side-by-side treatment tables.",
          "Two therapists working in parallel.",
          "A choice of five styles for each partner — Aroma, Swedish, Balinese, Deep Tissue or Candle.",
          "Published durations and prices: 60 min at ₹6,500, 90 min at ₹7,500, 120 min at ₹12,500.",
        ],
      },
      {
        h: "Small things that make it better",
        body: [
          "Mention health notes (pregnancy, allergies, injuries) in your enquiry so therapists are ready.",
          "Arrive ten minutes early to settle in together.",
          "Plan a quiet dinner afterward rather than a busy evening.",
          "If this is for an anniversary or birthday, mention it so the room setup can match the mood.",
        ],
      },
      {
        h: "Booking tips",
        body: [
          "The couples suite is a limited-capacity room — WhatsApp a day or two in advance to confirm your preferred slot.",
          "Weekend afternoons and evenings are the busiest.",
        ],
      },
    ],
    relatedSlugs: ["first-spa-visit", "best-spa-in-bhubaneswar"],
  },

  "first-spa-visit": {
    title: "What to expect during your first spa visit",
    seoTitle: "First Spa Visit Guide | What to Expect",
    description:
      "A friendly, honest guide to what happens on your first spa visit — arriving, consultation, during the session, after, and the small questions no one answers.",
    read: "5 min",
    kicker: "A friendly starting point",
    intro:
      "A first spa visit tends to go better when you know what the shape of it looks like. Nothing dramatic, nothing to be nervous about — just a short, calm sequence. Here is what to expect, start to finish.",
    sections: [
      {
        h: "Before you arrive",
        body: [
          "Avoid heavy meals in the hour before. A glass of water is fine.",
          "Dress in something comfortable; you'll be given clean clothing or disposables as needed.",
          "Share any health conditions, pregnancy or injuries in your enquiry.",
        ],
      },
      {
        h: "On arrival",
        body: [
          "A short reception — you'll settle in and complete a brief consultation.",
          "The therapist asks about your preferred pressure, areas to focus on, and any sensitivities.",
          "You'll be shown to a treatment room; everything is private.",
        ],
      },
      {
        h: "During the session",
        body: [
          "Pressure is adjustable at any point — just say so. The therapist will keep checking in.",
          "If you feel too warm, too cold, or want music softer, you can ask.",
          "If you need to pause, that's also fine.",
        ],
      },
      {
        h: "Afterwards",
        body: [
          "A few quiet minutes to rest. Drink water.",
          "Give yourself a gentle evening if you can — screens dim, dinner light.",
          "A mild sense of unwinding over the next few hours is normal.",
        ],
      },
    ],
    relatedSlugs: ["swedish-vs-deep-tissue", "best-spa-in-bhubaneswar"],
  },
};

export const GUIDE_SLUGS = Object.keys(GUIDE_ARTICLES);
export const getGuide = (slug) => GUIDE_ARTICLES[slug];
